import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext.jsx";
import { ChevronUp, ChevronDown, ChevronsUpDown, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge.jsx";
import { CardHeaderActions } from "@/components/ui/card-header-actions.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip.jsx";
import TablePagination from "@/components/ui/table-pagination.jsx";
import { cardStyles, infoTooltipIcon } from "@/lib/status-styles.js";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { getStatusBadgeClass, getStatusDotClass } from "@/lib/status-utils.js";
import { formatTimestamp, formatDateTimeWithSeconds } from "@/lib/datetime-utils.js";
import { getStatusDisplayText } from "@/lib/formatting-utils.js";
import { sortAssetsByColumn } from "@/lib/asset-data-utils.js";
import { mathUtils, cn } from "@/lib/utils.js";
import {
  parseTableStateFromSearchString,
  buildTableStateSearchParams,
} from "@/lib/table-url-state.js";
import { LoadingCard, LoadingText, LoadingGrid } from "./ui/loading.jsx";
import { useColumnPreferences } from "@/hooks/useColumnPreferences.js";
import { ColumnVisibilityPopover } from "@/components/ui/column-visibility-popover.jsx";
import { downloadFleetTableCSV } from "@/lib/csv-utils.js";

/**
 * @typedef {Object} FleetTableProps
 * @property {Object} filters - Filter configuration
 * @property {string[]} filters.vendor - Vendor filter values
 * @property {string[]} filters.location - Location filter values
 * @property {string[]} filters.telemetrySource - Telemetry source filter values
 * @property {boolean|null} filters.optimizationEnabled - true | false | null (null = all)
 * @property {string[]} filters.status - Status filter values
 * @property {string[]|null} filters.assetNumbers - Selected asset numbers (null = all)
 * @property {string} search - Search term
 * @property {Function} onSearchChange - Callback when search term changes
 */

const FLEET_VALID_SORT_COLUMNS = new Set([
  "assetNumber",
  "lease",
  "status",
  "location",
  "vendor",
  "telemetrySource",
  "optimizationEnabled",
  "lastFaultTime",
  "lastUpdated",
  "foreman",
  "customer",
  "horsepower",
  "compressorModel",
  "engineModel",
  "latitude",
  "longitude",
]);

// Helpers to get csv value & display value when they are the same
const getStatus = (asset) => getStatusDisplayText(asset.status);

/** Empty/missing cells match mock with a dash. */
const displayOrDash = (value) =>
  value == null || value === "" ? "-" : value;

/** Shared body cell sizing — compact mock density with readable padding. */
const fleetCell = (...extra) =>
  cn("text-sm text-foreground font-normal py-3 px-4", ...extra);

/**
 * Column descriptor array. Order here determines display order.
 * render(asset, ctx) returns a <TableCell>.
 * renderHead(ctx) returns a <TableHead>.
 */
const FLEET_COLUMNS = [
  {
    key: "assetNumber",
    label: "Asset Number",
    locked: true,
    defaultVisible: true,
    sortKey: "assetNumber",
    renderHead: (ctx) => (
      <TableHead
        key='assetNumber'
        className={cn(cardStyles.tableHead, "w-44 min-w-[10rem] z-20")}
        onClick={() => ctx.handleSort("assetNumber")}
        data-testid='header-assetnumber'>
        <div className='flex items-center'>
          Asset Number
          {ctx.getSortIcon("assetNumber")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => {
      const isOptimizationEnabled = asset.optimizationEnabled;
      return (
        <TableCell
          key='assetNumber'
          className={fleetCell(
            "w-44 min-w-[10rem] relative",
            isOptimizationEnabled && "pl-5"
          )}>
          {isOptimizationEnabled && (
            <div
              className='absolute left-0 top-0 bottom-0 w-[3px] bg-brand-cyan z-0'
              aria-hidden
            />
          )}
          <span className='relative z-0'>
            {displayOrDash(asset.assetNumber)}
          </span>
        </TableCell>
      );
    },
  },
  {
    key: "lease",
    label: "Lease Name",
    defaultVisible: true,
    sortKey: "lease",
    renderHead: (ctx) => (
      <TableHead
        key='lease'
        className={cn(cardStyles.tableHead, "w-28 min-w-[6.5rem]")}
        onClick={() => ctx.handleSort("lease")}
        data-testid='header-lease'>
        <div className='flex items-center'>
          Lease Name
          {ctx.getSortIcon("lease")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell
        key='lease'
        className={fleetCell("w-28 min-w-[6.5rem]")}>
        {displayOrDash(asset.lease)}
      </TableCell>
    ),
  },
  {
    key: "status",
    label: "Status",
    defaultVisible: true,
    sortKey: "status",
    csvValue: (asset) => getStatus(asset),
    renderHead: (ctx) => (
      <TableHead
        key='status'
        className={cn(cardStyles.tableHead, "w-36 min-w-[8rem]")}
        onClick={() => ctx.handleSort("status")}
        data-testid='header-status'>
        <div className='flex items-center'>
          Status
          {ctx.getSortIcon("status")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='status' className={fleetCell("w-36 min-w-[8rem]")}>
        <Badge variant='status' className={getStatusBadgeClass(asset.status)}>
          <div
            className={getStatusDotClass(
              asset.status,
              "w-1.5 h-1.5 rounded-full"
            )}
          />
          {getStatus(asset)}
        </Badge>
      </TableCell>
    ),
  },
  {
    key: "vendor",
    label: "Vendor",
    defaultVisible: true,
    sortKey: "vendor",
    renderHead: (ctx) => (
      <TableHead
        key='vendor'
        className={cn(cardStyles.tableHead, "w-32 min-w-[7rem]")}
        onClick={() => ctx.handleSort("vendor")}
        data-testid='header-vendor'>
        <div className='flex items-center'>
          Vendor
          {ctx.getSortIcon("vendor")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='vendor' className={fleetCell("w-32 min-w-[7rem]")}>
        {displayOrDash(asset.vendor)}
      </TableCell>
    ),
  },
  {
    key: "location",
    label: "Location",
    defaultVisible: true,
    sortKey: "location",
    renderHead: (ctx) => (
      <TableHead
        key='location'
        className={cn(cardStyles.tableHead, "w-32 min-w-[7rem]")}
        onClick={() => ctx.handleSort("location")}
        data-testid='header-location'>
        <div className='flex items-center'>
          Location
          {ctx.getSortIcon("location")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='location' className={fleetCell("w-32 min-w-[7rem]")}>
        {displayOrDash(asset.location)}
      </TableCell>
    ),
  },
  {
    key: "telemetrySource",
    label: "Telemetry",
    defaultVisible: true,
    sortKey: "telemetrySource",
    renderHead: (ctx) => (
      <TableHead
        key='telemetrySource'
        className={cn(cardStyles.tableHead, "w-28 min-w-[6.5rem]")}
        onClick={() => ctx.handleSort("telemetrySource")}
        data-testid='header-telemetry'>
        <div className='flex items-center'>
          Telemetry
          {ctx.getSortIcon("telemetrySource")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell
        key='telemetrySource'
        className={fleetCell("w-28 min-w-[6.5rem] capitalize")}>
        {displayOrDash(asset.telemetrySource)}
      </TableCell>
    ),
  },
  {
    key: "optimizationEnabled",
    label: "Optimization Enabled",
    defaultVisible: true,
    sortKey: "optimizationEnabled",
    csvValue: (asset) => (asset.optimizationEnabled ? "Yes" : "No"),
    renderHead: (ctx) => (
      <TableHead
        key='optimizationEnabled'
        className={cn(cardStyles.tableHead, "w-36 min-w-[8.5rem]")}
        onClick={() => ctx.handleSort("optimizationEnabled")}
        data-testid='header-optimization-enabled'>
        <div className='flex items-center'>
          Optimization Enabled
          {ctx.getSortIcon("optimizationEnabled")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell
        key='optimizationEnabled'
        className={fleetCell("w-36 min-w-[8.5rem]")}>
        <Badge
          variant='secondary'
          className='text-xs font-medium rounded-full bg-muted text-muted-foreground border border-border px-2.5 py-0.5 dark:bg-[#2a2e37] dark:text-[#9a9ea7] dark:border-[#373c42]'>
          {asset.optimizationEnabled ? "Yes" : "No"}
        </Badge>
      </TableCell>
    ),
  },
  {
    key: "lastFault",
    label: "Last Fault",
    defaultVisible: true,
    csvValue: (asset) => asset.lastFault || "",
    renderHead: () => (
      <TableHead
        key='lastFault'
        className={cn(cardStyles.tableHead, "min-w-[14rem] w-64")}>
        Last Fault
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell
        key='lastFault'
        className={fleetCell("min-w-[14rem] w-64")}>
        {displayOrDash(asset.lastFault)}
      </TableCell>
    ),
  },
  {
    key: "lastFaultTime",
    label: "Last Fault Date/Time",
    defaultVisible: true,
    sortKey: "lastFaultTime",
    csvValue: (asset) =>
      asset.lastFaultTime ? formatDateTimeWithSeconds(asset.lastFaultTime) : "",
    renderHead: (ctx) => (
      <TableHead
        key='lastFaultTime'
        className={cn(cardStyles.tableHead, "w-40 min-w-[9rem]")}
        onClick={() => ctx.handleSort("lastFaultTime")}
        data-testid='header-time-since-fault'>
        <div className='flex items-center gap-1'>
          <span>Last Fault Date/Time</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                className='inline-flex items-center justify-center cursor-help'
                onClick={(e) => e.stopPropagation()}
                aria-label='Time since last fault information'>
                <Info className={cn("h-3.5 w-3.5", infoTooltipIcon)} />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side='top'
              sideOffset={8}
              className='max-w-xs p-3 text-xs'>
              <p>
                This shows the time elapsed since the asset's last fault
                occurred. This is different from "Last Updated", which shows
                when telemetry data was last received. For example, an asset
                may have faulted 5 minutes ago but received data updates 1
                minute ago. For assets that are running, the last fault is
                limited to the last 30 days.
              </p>
            </TooltipContent>
          </Tooltip>
          {ctx.getSortIcon("lastFaultTime")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell
        key='lastFaultTime'
        className={fleetCell("w-40 min-w-[9rem]")}>
        {asset.lastFaultTime ? formatTimestamp(asset.lastFaultTime) : "-"}
      </TableCell>
    ),
  },
  {
    key: "lastUpdated",
    label: "Last Updated",
    defaultVisible: true,
    sortKey: "lastUpdated",
    csvValue: (asset) => formatDateTimeWithSeconds(asset.lastUpdated),
    renderHead: (ctx) => (
      <TableHead
        key='lastUpdated'
        className={cn(cardStyles.tableHead, "w-36 min-w-[8rem]")}
        onClick={() => ctx.handleSort("lastUpdated")}
        data-testid='header-lastupdated'>
        <div className='flex items-center'>
          Last Updated
          {ctx.getSortIcon("lastUpdated")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell
        key='lastUpdated'
        className={fleetCell("w-36 min-w-[8rem]")}>
        {asset.lastUpdated ? formatTimestamp(asset.lastUpdated) : "-"}
      </TableCell>
    ),
  },
  // Optional / default-off columns
  {
    key: "foreman",
    label: "Foreman",
    defaultVisible: false,
    sortKey: "foreman",
    csvValue: (asset) => asset.foreman || "",
    renderHead: (ctx) => (
      <TableHead
        key='foreman'
        className={cn(cardStyles.tableHead, "w-40")}
        onClick={() => ctx.handleSort("foreman")}
        data-testid='header-foreman'>
        <div className='flex items-center'>
          Foreman
          {ctx.getSortIcon("foreman")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='foreman' className={fleetCell("w-40")}>
        {displayOrDash(asset.foreman)}
      </TableCell>
    ),
  },
  {
    key: "customer",
    label: "Customer",
    defaultVisible: false,
    sortKey: "customer",
    csvValue: (asset) => asset.customer || "",
    renderHead: (ctx) => (
      <TableHead
        key='customer'
        className={cn(cardStyles.tableHead, "w-40")}
        onClick={() => ctx.handleSort("customer")}
        data-testid='header-customer'>
        <div className='flex items-center'>
          Customer
          {ctx.getSortIcon("customer")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='customer' className={fleetCell("w-40")}>
        {displayOrDash(asset.customer)}
      </TableCell>
    ),
  },
  {
    key: "horsepower",
    label: "Horsepower",
    defaultVisible: false,
    sortKey: "horsepower",
    csvValue: (asset) => (asset.horsepower != null ? asset.horsepower : ""),
    renderHead: (ctx) => (
      <TableHead
        key='horsepower'
        className={cn(cardStyles.tableHead, "w-32")}
        onClick={() => ctx.handleSort("horsepower")}
        data-testid='header-horsepower'>
        <div className='flex items-center'>
          Horsepower
          {ctx.getSortIcon("horsepower")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='horsepower' className={fleetCell("w-32")}>
        {asset.horsepower != null ? asset.horsepower : "-"}
      </TableCell>
    ),
  },
  {
    key: "compressorModel",
    label: "Compressor Model",
    defaultVisible: false,
    sortKey: "compressorModel",
    csvValue: (asset) => asset.compressorModel || "",
    renderHead: (ctx) => (
      <TableHead
        key='compressorModel'
        className={cn(cardStyles.tableHead, "w-48")}
        onClick={() => ctx.handleSort("compressorModel")}
        data-testid='header-compressor-model'>
        <div className='flex items-center'>
          Compressor Model
          {ctx.getSortIcon("compressorModel")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='compressorModel' className={fleetCell("w-48")}>
        {displayOrDash(asset.compressorModel)}
      </TableCell>
    ),
  },
  {
    key: "engineModel",
    label: "Engine Model",
    defaultVisible: false,
    sortKey: "engineModel",
    csvValue: (asset) => asset.engineModel || "",
    renderHead: (ctx) => (
      <TableHead
        key='engineModel'
        className={cn(cardStyles.tableHead, "w-48")}
        onClick={() => ctx.handleSort("engineModel")}
        data-testid='header-engine-model'>
        <div className='flex items-center'>
          Engine Model
          {ctx.getSortIcon("engineModel")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='engineModel' className={fleetCell("w-48")}>
        {displayOrDash(asset.engineModel)}
      </TableCell>
    ),
  },
  {
    key: "latitude",
    label: "Latitude",
    defaultVisible: false,
    sortKey: "latitude",
    csvValue: (asset) => (asset.latitude != null ? asset.latitude : ""),
    renderHead: (ctx) => (
      <TableHead
        key='latitude'
        className={cn(cardStyles.tableHead, "w-32")}
        onClick={() => ctx.handleSort("latitude")}
        data-testid='header-latitude'>
        <div className='flex items-center'>
          Latitude
          {ctx.getSortIcon("latitude")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='latitude' className={fleetCell("w-32 font-mono")}>
        {asset.latitude != null ? asset.latitude : "-"}
      </TableCell>
    ),
  },
  {
    key: "longitude",
    label: "Longitude",
    defaultVisible: false,
    sortKey: "longitude",
    csvValue: (asset) => (asset.longitude != null ? asset.longitude : ""),
    renderHead: (ctx) => (
      <TableHead
        key='longitude'
        className={cn(cardStyles.tableHead, "w-32")}
        onClick={() => ctx.handleSort("longitude")}
        data-testid='header-longitude'>
        <div className='flex items-center'>
          Longitude
          {ctx.getSortIcon("longitude")}
        </div>
      </TableHead>
    ),
    renderCell: (asset) => (
      <TableCell key='longitude' className={fleetCell("w-32 font-mono")}>
        {asset.longitude != null ? asset.longitude : "-"}
      </TableCell>
    ),
  },
];

const FLEET_DEFAULT_VISIBLE_KEYS = FLEET_COLUMNS
  .filter((c) => c.defaultVisible)
  .map((c) => c.key);

const FLEET_ALL_KEYS = FLEET_COLUMNS.map((c) => c.key);

function fleetAssetUrl(path, page, perPage, sortColumn, sortDirection) {
  const params = new URLSearchParams();
  params.set("from", "overview");
  const tableParams = buildTableStateSearchParams({
    page,
    itemsPerPage: perPage,
    sortColumn,
    sortDirection,
  });
  for (const [k, v] of tableParams) params.set(k, v);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Fleet Table component that displays compressor data with filtering and pagination
 * @param {FleetTableProps} props - Component props
 * @returns {JSX.Element} Fleet table with search, filters, and pagination
 */
export default function FleetTable({ filters, search = "", onSearchChange }) {
  const locationSearch = useSearch();
  const fromUrl = parseTableStateFromSearchString(
    locationSearch,
    FLEET_VALID_SORT_COLUMNS
  );

  /** @type {[number, Function]} */
  const [currentPage, setCurrentPage] = useState(fromUrl.page);

  /** @type {[any, Function]} */
  const [, setLocation] = useLocation();

  /** @type {[string | null, Function]} */
  const [sortColumn, setSortColumn] = useState(fromUrl.sortColumn);

  /** @type {['asc' | 'desc', Function]} */
  const [sortDirection, setSortDirection] = useState(fromUrl.sortDirection);

  /** @type {[number, Function]} */
  const [itemsPerPage, setItemsPerPage] = useState(fromUrl.itemsPerPage);

  const { visibleKeys, toggleColumn, resetToDefaults } = useColumnPreferences(
    "fleetTableColumns",
    FLEET_DEFAULT_VISIBLE_KEYS,
    FLEET_ALL_KEYS
  );

  /** True after user changes page or rows per page this mount; then we never prefer URL over state */
  const userHasChangedPaginationRef = useRef(false);

  // Sync pagination and sort state from URL when it changes (e.g. Back from asset)
  useEffect(() => {
    const parsed = parseTableStateFromSearchString(
      locationSearch,
      FLEET_VALID_SORT_COLUMNS
    );
    setCurrentPage(parsed.page);
    setItemsPerPage(parsed.itemsPerPage);
    setSortColumn(parsed.sortColumn);
    setSortDirection(parsed.sortDirection);
  }, [locationSearch]);

  // Keep URL in sync with pagination and sort so back-from-asset returns to same state.
  useEffect(() => {
    const urlSearch = window.location.search;
    const currentParams = new URLSearchParams(
      urlSearch?.startsWith("?") ? urlSearch.slice(1) : urlSearch || ""
    );
    const tableParams = buildTableStateSearchParams({
      page: currentPage,
      itemsPerPage,
      sortColumn,
      sortDirection,
    });
    // Apply table state: set params from tableParams and remove defaults so URL reflects current page/sort
    for (const [k, v] of tableParams) {
      currentParams.set(k, v);
    }
    currentParams.delete("optimizationEnabled");
    if (currentPage === 1) currentParams.delete("page");
    if (itemsPerPage === 10) currentParams.delete("perPage");
    if (!sortColumn) {
      currentParams.delete("sort");
      currentParams.delete("dir");
    }
    const query = currentParams.toString();
    const target = query ? `/?${query}` : "/";
    const current = window.location.pathname + (window.location.search || "");
    if (current !== target) {
      const currentHasParams = urlSearch.length > 0;
      const currentUrlHasPage = currentParams.has("page");
      const targetWouldDropPage = currentPage === 1; // we don't add page when 1
      // Prefer URL when we would drop the page (landing back-from-asset with ?page=2) or wipe
      // params, and the user hasn't changed page/rows/sort this mount.
      const shouldPreferUrl =
        !userHasChangedPaginationRef.current
        && currentHasParams
        && (target === "/" || (currentUrlHasPage && targetWouldDropPage));
      if (shouldPreferUrl) {
        const parsed = parseTableStateFromSearchString(
          urlSearch,
          FLEET_VALID_SORT_COLUMNS
        );
        setCurrentPage(parsed.page);
        setItemsPerPage(parsed.itemsPerPage);
        setSortColumn(parsed.sortColumn);
        setSortDirection(parsed.sortDirection);
        return;
      }
      setLocation(target, { replace: true });
    }
  }, [currentPage, itemsPerPage, sortColumn, sortDirection, setLocation]);

  const { isLoading } = useAssets();
  const { filteredAssets } = useFilteredAssets(filters, search);

  // Set up interval to update time display every minute for real-time updates
  const [updateTrigger, setUpdateTrigger] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger((prev) => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  /**
   * Handles column header click for sorting
   * @param {string} column - Column name to sort by
   */
  const handleSort = (column) => {
    userHasChangedPaginationRef.current = true;
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // When a column is hidden and it's the active sort column, clear the sort.
  const handleToggleColumn = (key) => {
    const col = FLEET_COLUMNS.find((c) => c.key === key);
    if (col?.sortKey && col.sortKey === sortColumn && visibleKeys.includes(key)) {
      setSortColumn(null);
      setSortDirection("asc");
    }
    toggleColumn(key);
  };

  // Apply sorting to filtered compressors
  // Note: updateTrigger is included to force re-render for real-time time updates
  const sortedCompressors = useMemo(() => {
    return sortAssetsByColumn(filteredAssets, sortColumn, sortDirection);
  }, [filteredAssets, sortColumn, sortDirection, updateTrigger]);

  const totalPages = mathUtils.ceil(sortedCompressors.length / itemsPerPage);

  // Reset to page 1 if current page exceeds total pages (e.g., after filtering)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const adjustedCurrentPage = currentPage > totalPages ? 1 : currentPage;
  const startIndex = (adjustedCurrentPage - 1) * itemsPerPage;
  const paginatedCompressors = sortedCompressors.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  /**
   * Gets the sort icon for a column
   * @param {string} column - Column name
   * @returns {JSX.Element} Sort icon component
   */
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? (
        <ChevronUp className='h-4 w-4 ml-1 text-muted-foreground' />
      ) : (
        <ChevronDown className='h-4 w-4 ml-1 text-muted-foreground' />
      );
    }
    return (
      <ChevronsUpDown className='h-4 w-4 ml-1 text-muted-foreground opacity-50' />
    );
  };

  // Reset to page 1 when filters or search change.
  const filterKey = JSON.stringify({
    vendor: filters.vendor,
    location: filters.location,
    telemetrySource: filters.telemetrySource,
    optimizationEnabled: filters.optimizationEnabled,
    status: filters.status,
    assetNumbers: filters.assetNumbers,
    horsepowerRange: filters.horsepowerRange,
    foreman: filters.foreman,
    customer: filters.customer,
    engineModel: filters.engineModel,
    compressorModel: filters.compressorModel,
  });
  const prevFilterAndSearchRef = useRef({ filterKey, search });
  useEffect(() => {
    const prev = prevFilterAndSearchRef.current;
    if (prev.filterKey !== filterKey || prev.search !== search) {
      prevFilterAndSearchRef.current = { filterKey, search };
      setCurrentPage(1);
    }
  }, [filterKey, search]);

  // When user changes rows per page, reset to page 1 in the same update so URL sync
  // never writes the old page and then "prefer URL" undoes the reset
  const handleItemsPerPageChange = (value) => {
    userHasChangedPaginationRef.current = true;
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    userHasChangedPaginationRef.current = true;
    setCurrentPage(page);
  };

  const ctx = { handleSort, getSortIcon };

  // Columns to render: locked ones always included, others filtered by visibleKeys
  const visibleColumns = FLEET_COLUMNS.filter(
    (col) => col.locked || visibleKeys.includes(col.key)
  );

  if (isLoading) {
    return (
      <LoadingCard
        data-testid='card-downtime-loading'
        className='flex flex-col flex-1 min-h-0 h-full'>
        <div className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 rounded-t-lg border-b border-border'>
          <div>
            <LoadingText />
          </div>
        </div>
        <div className='flex-1 min-h-0 p-6 overflow-hidden'>
          <LoadingGrid count={8} />
        </div>
      </LoadingCard>
    );
  }

  return (
    <Card className='bg-card border border-border shadow-sm rounded-lg overflow-hidden flex flex-col flex-1 min-h-0 h-full'>
      <CardHeaderActions
        title='Compressor Fleet Status'
        actions={[
          {
            id: 1,
            text: "Export (.csv)",
            executeAction: () => downloadFleetTableCSV(visibleColumns, sortedCompressors),
            actionType: "Download",
          },
          {
            id: 2,
            element: (
              <ColumnVisibilityPopover
                columns={FLEET_COLUMNS}
                visibleKeys={visibleKeys}
                onToggle={handleToggleColumn}
                onReset={resetToDefaults}
              />
            ),
          },
        ]}
        searchEnabled={true}
        searchValue={search}
        onSearchChange={onSearchChange}
        searchPlaceholder='Search compressors...'
        searchTestId='input-search-compressors'
        titleTestId='title-fleet-status'
      />

      <CardContent
        className={cn(
          cardStyles.content,
          "flex flex-col flex-1 min-h-0"
        )}>
        <div
          className={cn(
            cardStyles.tableContainer,
            cardStyles.tableFillBody
          )}>
          <Table className='w-full text-sm'>
            <TableHeader className={cardStyles.tableHeader}>
              <TableRow className={cardStyles.tableRow}>
                {visibleColumns.map((col) => col.renderHead(ctx))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCompressors.map((compressor) => (
                <TableRow
                  key={compressor.id}
                  className={cn(
                    cardStyles.bodyRow,
                    "dark:hover:bg-muted/80 cursor-pointer"
                  )}
                  onClick={() =>
                    setLocation(
                      fleetAssetUrl(
                        `/asset/${encodeURIComponent(compressor.id)}/overview`,
                        currentPage,
                        itemsPerPage,
                        sortColumn,
                        sortDirection
                      )
                    )
                  }
                  data-testid={`row-compressor-${compressor.assetNumber}`}>
                  {visibleColumns.map((col) => col.renderCell(compressor, ctx))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className='shrink-0'>
          <TablePagination
            currentPage={adjustedCurrentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={sortedCompressors.length}
            startIndex={startIndex}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsLabel='results'
          />
        </div>
      </CardContent>
    </Card>
  );
}
